import React, { Fragment, ReactNode, useRef, useState } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { useIsSmallScreen } from '../../../helper/LayoutHelper';
import Dialog from '../Dialog/Dialog';

interface Props {
    /** The inline trigger row (formatted date + icon). */
    trigger: ReactNode;
    /** When true the trigger is shown but cannot be opened (fixed / single-day). */
    readOnly?: boolean;
    /** Dialog title on mobile. */
    title?: string;
    /** Accessible label for the trigger button, e.g. "Ablesedatum 01.06.2026, Kalender öffnen". */
    ariaLabel?: string;
    /** Render-prop for the calendar; receives a `close` it should call after a selection. */
    children: (close: () => void, isMobile?: boolean) => ReactNode;
}

/**
 * Adaptive host for the calendar:
 *  - Mobile  → the existing fullscreen `Dialog` (X / backdrop / ESC close; no bottom-sheet, no gestures).
 *  - Desktop → a Headless-UI `Popover` anchored under the trigger.
 *
 * A tap on a day calls `close()` from the render-prop, so selection closes the overlay.
 */
const DatePickerOverlay = ({ trigger, readOnly = false, title, ariaLabel, children }: Props) => {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [openAbove, setOpenAbove] = useState(false);
    const isSmallScreen = useIsSmallScreen();

    if (readOnly) {
        return <div className={'datepicker-trigger datepicker-trigger--readonly'}>{trigger}</div>;
    }

    if (isSmallScreen) {
        return (
            <>
                <button
                    type={'button'}
                    className={'datepicker-trigger block w-full p-0 text-left focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'}
                    aria-haspopup={'dialog'}
                    aria-label={ariaLabel}
                    onClick={() => setOpen(true)}
                >
                    {trigger}
                </button>
                <Dialog visible={open} closable={true} fullscreen={true} title={title} onClose={() => setOpen(false)}>
                    <div className={'w-full max-w-md mx-auto'}>
                        {open && children(() => setOpen(false), true)}
                    </div>
                </Dialog>
            </>
        );
    }

    const handleTriggerClick = () => {
        // rAF lets concurrent dropdown-close reflows settle before measuring position.
        requestAnimationFrame(() => {
            if (!triggerRef.current) return;
            const rect = triggerRef.current.getBoundingClientRect();
            setOpenAbove(rect.top > window.innerHeight - rect.bottom);
        });
    };

    // Desktop: anchored popover (Headless-UI manages open state + outside-click/ESC).
    return (
        <Popover className={'datepicker-popover'}>
            {({ close }) => (
                <>
                    <Popover.Button
                        ref={triggerRef}
                        className={'datepicker-trigger block w-full text-left focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'}
                        aria-haspopup={'dialog'}
                        aria-label={ariaLabel}
                        onClick={handleTriggerClick}
                    >
                        {trigger}
                    </Popover.Button>
                    <Transition
                        as={Fragment}
                        enter={'transition ease-out duration-150'}
                        enterFrom={`opacity-0 ${openAbove ? 'translate-y-1' : '-translate-y-1'}`}
                        enterTo={'opacity-100 translate-y-0'}
                        leave={'transition ease-in duration-100'}
                        leaveFrom={'opacity-100 translate-y-0'}
                        leaveTo={`opacity-0 ${openAbove ? 'translate-y-1' : '-translate-y-1'}`}
                    >
                        <Popover.Panel
                            className={`datepicker-popover__panel${openAbove ? ' datepicker-popover__panel--above' : ''}`}
                        >
                            {children(close)}
                        </Popover.Panel>
                    </Transition>
                </>
            )}
        </Popover>
    );
};

export default DatePickerOverlay;
